import { useEffect, useState } from 'react'
import { Button, Input, Modal, Select, Textarea, useToast } from '@/design/components'
import { ApiError } from '@/lib/api'
import { useCaseOptions, useCreateTask, useLawyers } from '../hooks'

export interface NewTaskModalProps {
  open: boolean
  onClose: () => void
  /** Prefill z karty sprawy (`/zadania?sprawa=<id>&nowa=1`). */
  initialCaseId?: number | null
}

/** „Nowe zadanie" — przydział pracy w sprawie (`created_by` = zalogowany). */
export function NewTaskModal({ open, onClose, initialCaseId }: NewTaskModalProps) {
  const toast = useToast()
  const cases = useCaseOptions(open)
  const lawyers = useLawyers(open)
  const create = useCreateTask()

  const [caseId, setCaseId] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [assigneeId, setAssigneeId] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Prefill sprawy przychodzi asynchronicznie z query param — odśwież formularz po otwarciu.
  useEffect(() => {
    if (open) {
      setCaseId(initialCaseId ? String(initialCaseId) : '')
      setTitle('')
      setDescription('')
      setDueDate('')
      setAssigneeId('')
      setError(null)
    }
  }, [open, initialCaseId])

  const submit = async () => {
    setError(null)
    if (!caseId) {
      setError('Wybierz sprawę.')
      return
    }
    if (!title.trim()) {
      setError('Podaj tytuł zadania.')
      return
    }
    try {
      await create.mutateAsync({
        case_id: Number(caseId),
        title: title.trim(),
        description: description.trim() ? description.trim() : null,
        due_date: dueDate || null,
        assignee_id: assigneeId ? Number(assigneeId) : null,
      })
      toast.success('Zadanie dodane')
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : 'Nie udało się dodać zadania.')
    }
  }

  const sortedCases = [...(cases.data?.items ?? [])].sort((a, b) =>
    a.signature.localeCompare(b.signature, 'pl'),
  )

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Nowe zadanie"
      subtitle="Zadanie żyje w sprawie — wybierz sprawę i (opcjonalnie) osobę odpowiedzialną."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Anuluj
          </Button>
          <Button variant="primary" onClick={submit} loading={create.isPending}>
            Dodaj zadanie
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Select
            label="Sprawa"
            value={caseId}
            onChange={(event) => setCaseId(event.target.value)}
            options={[
              { value: '', label: 'wybierz sprawę…' },
              ...sortedCases.map((item) => ({
                value: String(item.id),
                label: `${item.signature} · ${item.client_name}`,
              })),
            ]}
          />
        </div>
        <div className="col-span-2">
          <Input
            label="Tytuł zadania"
            value={title}
            placeholder="np. Przygotować odpowiedź na pozew"
            onChange={(event) => setTitle(event.target.value)}
          />
        </div>
        <Input
          label="Termin (opcjonalny)"
          type="date"
          value={dueDate}
          onChange={(event) => setDueDate(event.target.value)}
        />
        <Select
          label="Przypisany"
          value={assigneeId}
          onChange={(event) => setAssigneeId(event.target.value)}
          options={[
            { value: '', label: 'nieprzypisane' },
            ...(lawyers.data ?? []).map((lawyer) => ({
              value: String(lawyer.id),
              label: lawyer.full_name,
            })),
          ]}
        />
        <div className="col-span-2">
          <Textarea
            label="Opis"
            rows={3}
            value={description}
            placeholder="Zakres pracy, materiały wejściowe, oczekiwany rezultat…"
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>
      </div>
      {error ? <p className="mt-3 text-micro text-danger">{error}</p> : null}
    </Modal>
  )
}
